import { Sheet, SheetContent, SheetHeader, SheetTitle } from "./ui/sheet";
import { Badge } from "./ui/badge";
import { Card, CardContent, CardHeader, CardTitle } from "./ui/card";
import { Mail, Phone, Calendar, QrCode, MapPin } from "lucide-react";
import { GDRIVE_BASE_URL, usersApi } from "../utils/api";
import React, { useEffect, useState } from "react";
import { formatDateTime } from "../utils/formatters";
import { Button } from "./ui/button";
import FullScreenQrImageModal from "./FullScreenQrImageModal";
import Image from "./Image";
import { formatAttributionValue, formatLocation, getAttribution } from "../utils/attribution";

export function UserDetailDrawer({ userId, isOpen, onClose }) {
  const [userData, setUserData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isImageModalOpen, setIsImageModalOpen] = useState(false);
  const [selectedQRImage, setSelectedQRImage] = useState(null);

  useEffect(() => {
    const fetchUserDetails = async () => {
      if (!userId) return;
      setLoading(true);
      setError(null);
      try {
        const response = await usersApi.getUserById(userId);
        setUserData(response.data);
      } catch (err) {
        console.error("Error fetching User details:", err);
        setError("Failed to load User details.");
      } finally {
        setLoading(false);
      }
    };

    if (isOpen && userId) {
      fetchUserDetails();
    }
  }, [userId, isOpen]);

  const handleQRImageClick = (code) => {
    setSelectedQRImage(code);
    setIsImageModalOpen(true);
  };

  const handleDownloadImage = async () => {
    if (!selectedQRImage) return;
    try {
      const response = await fetch(
        `${GDRIVE_BASE_URL}${selectedQRImage?.gdriveId || selectedQRImage?.gdrive_id}`
      );
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download =
        selectedQRImage?.batch_id +
        selectedQRImage?.code_number +
        "_qr-code.png";
      document.body.appendChild(a);
      a.click();
      window.URL.revokeObjectURL(url);
      document.body.removeChild(a);
    } catch (error) {
      console.error("Error downloading image:", error);
      try {
        window.open(`${GDRIVE_BASE_URL}${selectedQRImage?.gdriveId || selectedQRImage?.gdrive_id}`, "_blank");
      } catch (fallback) {
        console.error("Fallback also failed:", fallback);
      }
    }
  };

  const closeImageModal = () => {
    setIsImageModalOpen(false);
    setSelectedQRImage(null);
  };

  const attribution = getAttribution(userData);

  const renderTouch = (title, touch) => (
    <div className="space-y-2">
      <p className="font-medium text-sm">{title}</p>
      <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
        <span className="text-muted-foreground">Source</span><span>{formatAttributionValue(touch?.source)}</span>
        <span className="text-muted-foreground">Medium</span><span>{formatAttributionValue(touch?.medium)}</span>
        <span className="text-muted-foreground">Campaign</span><span>{formatAttributionValue(touch?.campaign)}</span>
        <span className="text-muted-foreground">Term</span><span>{formatAttributionValue(touch?.term)}</span>
        <span className="text-muted-foreground">Content</span><span>{formatAttributionValue(touch?.content)}</span>
        <span className="text-muted-foreground">Date</span><span>{formatAttributionValue(touch?.date)}</span>
      </div>
    </div>
  );

  if (!isOpen || !userId) return null; // Don't render if not open or no QR code selected

  return (
    <>
      <Sheet open={isOpen} onOpenChange={onClose}>
        <SheetContent className="w-[400px] sm:w-[540px]  max-h-[100vh] overflow-y-auto pb-4">
          <SheetHeader>
            <SheetTitle>User Details</SheetTitle>
          </SheetHeader>
          {loading ? (
            <div className="flex justify-center items-center py-8">
              <p className="text-muted-foreground">Loading User details...</p>
            </div>
          ) : error ? (
            <div className="flex justify-center items-center py-8">
              <p className="text-destructive">{error}</p>
            </div>
          ) : userData ? (
            <div className="mt-6 space-y-6">
              {/* Basic Information */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Basic Information</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 bg-primary rounded-full flex items-center justify-center">
                      <span className="text-primary-foreground text-sm font-medium">
                        {userData?.basicInfo?.name?.charAt(0)}
                      </span>
                    </div>
                    <div>
                      <p className="font-medium">{userData?.basicInfo?.name}</p>
                      <p className="text-sm text-muted-foreground">
                        User ID: {userData?.basicInfo?.userId}
                      </p>
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Mail className="w-4 h-4 text-muted-foreground" />
                    <span>{userData?.basicInfo?.email}</span>
                  </div>

                  <div className="flex items-center gap-2">
                    <Phone className="w-4 h-4 text-muted-foreground" />
                    <span>{userData?.basicInfo?.mobile}</span>
                  </div>

                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-muted-foreground" />
                    <span>
                      Registered on{" "}
                      {formatDateTime(userData?.basicInfo?.registeredOn)}
                    </span>
                  </div>
                </CardContent>
              </Card>

              {attribution && (
                <Card>
                  <CardHeader>
                    <CardTitle className="text-lg flex items-center gap-2">
                      <MapPin className="w-5 h-5" />
                      Acquisition & Location
                    </CardTitle>
                  </CardHeader>
                  <CardContent className="space-y-5">
                    {attribution.firstTouch && renderTouch("First Touch", attribution.firstTouch)}
                    {attribution.latestTouch && renderTouch("Latest Touch", attribution.latestTouch)}
                    {attribution.location && (
                      <div className="space-y-1 text-sm">
                        <p className="font-medium">Approximate location</p>
                        <p className="text-muted-foreground">{formatLocation(attribution.location)}</p>
                        <p className="text-xs text-muted-foreground">City, region, and country are IP-derived estimates.</p>
                      </div>
                    )}
                  </CardContent>
                </Card>
              )}

              {/* Assigned QR Codes */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg flex items-center gap-2">
                    <QrCode className="w-5 h-5" />
                    Assigned QR Codes ({userData?.assignedQRCodes?.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex flex-col gap-6">
                    {userData?.assignedQRCodes.map((qrCode, index) => (
                      <div
                        key={qrCode?.qrid || index}
                        className="border p-4 rounded-lg shadow-sm bg-gray-50 flex flex-col sm:flex-row gap-4 items-start"
                      >
                        <div className="flex-shrink-0 flex flex-col items-center gap-2">
                          <Image
                            fileId={qrCode.gdrive_id}
                            alt="QR Code"
                            className="w-16 h-16 object-cover rounded border-2 border-gray-200"
                            fallbackImage={""}
                          />
                          <Button
                            variant="secondary"
                            size="sm"
                            onClick={() => handleQRImageClick(qrCode)}
                            className="mt-1"
                          >
                            View
                          </Button>
                        </div>
                        <div className="flex-grow grid grid-cols-2 gap-2">
                          <div className="col-span-2 font-semibold text-gray-800 text-base">
                            {qrCode?.product_name}
                          </div>
                          <div className="col-span-1">
                            <span className="font-medium">Batch:</span>{" "}
                            {qrCode?.batch_id}
                          </div>
                          <div className="col-span-1">
                            <span className="font-medium">Code:</span>{" "}
                            {qrCode?.code_number}
                          </div>
                          <div className="col-span-1">
                            <span className="font-medium">Status:</span>{" "}
                            {qrCode?.status}
                          </div>
                          <div className="col-span-1">
                            <span className="font-medium">Active:</span>{" "}
                            {qrCode?.is_active ? "Yes" : "No"}
                          </div>
                          <div className="col-span-2 truncate">
                            <span className="font-medium">QR ID:</span>{" "}
                            {qrCode?.qrid}
                          </div>
                        </div>
                      </div>
                    ))}
                    {userData?.assignedQRCodes?.length === 0 && (
                      <p className="text-muted-foreground text-sm">
                        No QR codes assigned yet
                      </p>
                    )}
                  </div>
                </CardContent>
              </Card>

              {/* Recent Activity */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Recent Activity</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {userData?.recentActivity?.lastScan && (
                      <div className="flex justify-between text-sm">
                        <span>Last scan</span>
                        <span className="text-muted-foreground">
                          {userData?.recentActivity?.lastScan}
                        </span>
                      </div>
                    )}
                    {/* <div className="flex justify-between text-sm">
                    <span>Last login</span>
                    <span className="text-muted-foreground">
                      2024-01-15 09:15
                    </span>
                  </div> */}
                    <div className="flex justify-between text-sm">
                      <span>Total scans</span>
                      <span className="text-muted-foreground">
                        {userData?.recentActivity?.totalScans}
                      </span>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>
          ) : (
            <div className="flex justify-center items-center py-8">
              <p className="text-muted-foreground">
                No QR code details available.
              </p>
            </div>
          )}
        </SheetContent>
      </Sheet>
      {/* QR Image Modal - Full Screen Gallery */}
      <FullScreenQrImageModal
        open={isImageModalOpen}
        onOpenChange={setIsImageModalOpen}
        image={selectedQRImage}
        onDownload={handleDownloadImage}
        onClose={closeImageModal}
      />
    </>
  );
}
